import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NavigationService } from '../../services/navigation/navigation.service';
import { CacheService } from '../../services/cache/cache.service';
import { CommonService } from '../../services/common/common.service';
import { Column } from '../interfaces/column.interface';
import moment from 'moment';
import { PushDevicesDataService } from './push-devices-data.service';
import { PushDevice, PushDevicesResult } from '../interfaces';
import { PushNotificationsDataService } from './push-notifications-data.service';
import { PushNotification, PushNotificationsResult } from '../interfaces';
import { UsersDataService } from './users-data.service';
import { User, UsersResult } from '../interfaces';

@Injectable({
  providedIn: 'root'
})
export class PushMessagesColumnsService {

  public pushDevices: PushDevice[] = [];
  public pushNotifications: PushNotification[] = [];
  public users: User[] = [];
  public sendingStatusesOptions = [{
    value: 'Initialized',
    label: 'SENDINGSTATUSESINITIALIZED',
    css: 'primary'
  }, {
    value: 'Queued',
    label: 'SENDINGSTATUSESQUEUED',
    css: 'success'
  }, {
    value: 'Delivered',
    label: 'SENDINGSTATUSESDELIVERED',
    css: 'warning'
  }, {
    value: 'Failed',
    label: 'SENDINGSTATUSESFAILED',
    css: 'danger'
  }];
  public defaultParameters: {[param: string]: string | string[]} = {offset: '0', limit: '1000'};

  constructor(
    private translate: TranslateService,
    private navigation: NavigationService,
    private cache: CacheService,
    private common: CommonService,
    private pushDevicesDataService: PushDevicesDataService,
    private pushNotificationsDataService: PushNotificationsDataService,
    private usersDataService: UsersDataService,

  ) {
    this.getPushDevices()
    this.getPushNotifications()
    this.getUsers()
  }

  getPushDevices(offset: number = 0): void {
    const cacheKey: string = 'pushDevices.cache'
    const cache: any = this.cache.get(cacheKey);
    if (cache !== null && offset === 0) {
      this.pushDevices = cache.results
      return;
    }
    const parameters = Object.assign(this.defaultParameters, {offset: offset + ''});
    this.pushDevicesDataService.getPushDevices(parameters).subscribe((response: PushDevicesResult|any) => {
      if (response !== null) {
        if (offset === 0) {
          this.pushDevices = response.results;
        } else {
          this.pushDevices = this.pushDevices.concat(response.results);
        }
        if (response.inlineCount > this.pushDevices.length) {
          offset = offset + parseInt(this.defaultParameters.limit + '', 10);
          this.getPushDevices(offset);
        }
        response.results = this.pushDevices;
        this.cache.set(cacheKey, response);
      }
    });
  }

  getPushNotifications(offset: number = 0): void {
    const cacheKey: string = 'pushNotifications.cache'
    const cache: any = this.cache.get(cacheKey);
    if (cache !== null && offset === 0) {
      this.pushNotifications = cache.results
      return;
    }
    const parameters = Object.assign(this.defaultParameters, {offset: offset + ''});
    this.pushNotificationsDataService.getPushNotifications(parameters).subscribe((response: PushNotificationsResult|any) => {
      if (response !== null) {
        if (offset === 0) {
          this.pushNotifications = response.results;
        } else {
          this.pushNotifications = this.pushNotifications.concat(response.results);
        }
        if (response.inlineCount > this.pushNotifications.length) {
          offset = offset + parseInt(this.defaultParameters.limit + '', 10);
          this.getPushNotifications(offset);
        }
        response.results = this.pushNotifications;
        this.cache.set(cacheKey, response);
      }
    });
  }

  getUsers(offset: number = 0): void {
    const cacheKey: string = 'users.cache'
    const cache: any = this.cache.get(cacheKey);
    if (cache !== null && offset === 0) {
      this.users = cache.results
      return;
    }
    const parameters = Object.assign(this.defaultParameters, {offset: offset + ''});
    this.usersDataService.getUsers(parameters).subscribe((response: UsersResult|any) => {
      if (response !== null) {
        if (offset === 0) {
          this.users = response.results;
        } else {
          this.users = this.users.concat(response.results);
        }
        if (response.inlineCount > this.users.length) {
          offset = offset + parseInt(this.defaultParameters.limit + '', 10);
          this.getUsers(offset);
        }
        response.results = this.users;
        this.cache.set(cacheKey, response);
      }
    });
  }

  getColumns(): Column[] {
    return [
      { field: 'id', sortField: 'id', title: this.translate.instant('ID'), show: (this.navigation.getParamValue('pushMessages.id.showFiled', true) && true), displayInList: true, type: 'numericValue'},
      { field: 'pushDevice', title: this.translate.instant('PUSHDEVICE'), type: 'linkValue', filterData: this.pushDevices, show: (this.navigation.getParamValue('pushMessages.pushDeviceId.showFiled', true) && true), displayInList: true, filterField: 'pushDevice.id', displayField: 'appName', sortField: 'pushDevice.appName', url: 'push-devices' },
      { field: 'pushNotification', title: this.translate.instant('PUSHNOTIFICATION'), type: 'linkValue', filterData: this.pushNotifications, show: (this.navigation.getParamValue('pushMessages.pushNotificationId.showFiled', true) && true), displayInList: true, filterField: 'pushNotification.id', displayField: 'title', sortField: 'pushNotification.title', url: 'push-notifications' },
      { field: 'delivery', sortField: 'delivery', title: this.translate.instant('DELIVERY'), show: (this.navigation.getParamValue('pushMessages.delivery.showFiled', true) && true), displayInList: true, type: 'dateValue', valueFormatter: this.translate.instant('DATETIMEFORMAT')},
      { field: 'languageCode', sortField: 'languageCode', title: this.translate.instant('LANGUAGECODE'), show: (this.navigation.getParamValue('pushMessages.languageCode.showFiled', true) && true), displayInList: true, type: 'textValue'},
      { field: 'sendingStatus', sortField: 'sendingStatus', title: this.translate.instant('SENDINGSTATUS'), show: (this.navigation.getParamValue('pushMessages.sendingStatus.showFiled', true) && true), displayInList: true, type: 'enumValue', filterData: this.sendingStatusesOptions},
      { field: 'response', sortField: 'response', title: this.translate.instant('RESPONSE'), show: (this.navigation.getParamValue('pushMessages.response.showFiled', true) && true), displayInList: true, type: 'textValue'},
      { field: 'createdAt', sortField: 'createdAt', title: this.translate.instant('CREATEDAT'), show: (this.navigation.getParamValue('pushMessages.createdAt.showFiled', false) && true), displayInList: true, type: 'dateValue', valueFormatter: this.translate.instant('DATETIMEFORMAT')},
      { field: 'creatorUser', title: this.translate.instant('CREATORUSER'), type: 'linkValue', filterData: this.users, show: (this.navigation.getParamValue('pushMessages.creatorUser.showFiled', false) && true), displayInList: true, filterField: 'creatorUser.id', displayField: 'username', sortField: 'creatorUser.username', url: 'users' },
      { field: 'modifiedAt', sortField: 'modifiedAt', title: this.translate.instant('MODIFIEDAT'), show: (this.navigation.getParamValue('pushMessages.modifiedAt.showFiled', false) && true), displayInList: true, type: 'dateValue', valueFormatter: this.translate.instant('DATETIMEFORMAT')},
      { field: 'modifierUser', title: this.translate.instant('MODIFIERUSER'), type: 'linkValue', filterData: this.users, show: (this.navigation.getParamValue('pushMessages.modifierUser.showFiled', false) && true), displayInList: true, filterField: 'modifierUser.id', displayField: 'username', sortField: 'modifierUser.username', url: 'users' },
      { field: 'actions', title: this.translate.instant('ACTIONS'), type: 'static', show: true, displayInList: true, sortField: null},
    ];
  }

  format(fieldName: string, value: any) {
    if (fieldName.toLowerCase().indexOf('time') > -1) {
      return moment(value).format('YYYY-MM-DD HH:mm');
    } else if (fieldName.toLowerCase().indexOf('date') > -1) {
      return moment(value).format('YYYY-MM-DD');
    }
    return value;
  }

}
